import { FC, useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import styled from '@emotion/styled'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { Colors, Shapes } from 'src/components/theme'
import { SidebarEntityHeader } from 'src/v2/components/sidebar-entity-header'
import HeaderBG from 'src/assets/images/owner-reveal/header.svg'
import { getPropertiesDistribution, parseEntityDistribution } from 'src/v2/utils/entity'
import { formatString } from 'src/utils/format-string'
import { OWNER_REPORTS_MENU_ITEMS, PORTFOLIO_REPORTS_MENU_ITEMS } from 'src/v2/state/owner-reveal/insights'
import { SidebarInsightsMenu } from 'src/v2/components/sidebar-insights-menu'
import { useToggleParcelsLayer } from 'src/v2/views/owner-reveal/sidebar/use-toggle-parcels-layer'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { OwnerParcels } from 'src/v2/views/owner-reveal/sidebar/owner-parcels'
import { OwnerPortfolio } from 'src/v2/views/owner-reveal/sidebar/owner-portfolio'
import { Divider, Typography } from '@mui/material'
import { FlexColumn } from 'src/components/flex-containers'

import * as coreAtoms from 'src/v2/state/core'
import { MapExplorerPanelActions } from 'src/views/map-explorer/sidebar/panels/actions'
import { ErrorBoundary } from 'src/components/errors/error-boundary'

const ICON_BG_COLOR = Colors.primaryRed50
const ICON_BORDER_COLOR = Colors.primaryRed200

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ReportsContainer = styled(FlexColumn)`
  padding: ${Shapes.spacing[5]}px 0;
  gap: ${Shapes.spacing[4]}px;
`

const ReportsTitle = styled(Typography)`
  padding: 0 ${Shapes.spacing[4]}px;
`

const ReportsContent = styled(FlexColumn)`
  gap: ${Shapes.spacing[4]}px;
`

type MapExplorerSelectedOwnerProps = {
  showNavigationActions?: boolean
}

export const MapExplorerSelectedOwner: FC<MapExplorerSelectedOwnerProps> = ({ showNavigationActions = false }) => {
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const { clearSelected, getBackText, goBack } = useMapExplorerNavigation()

  useToggleParcelsLayer()

  const isOwnershipEnabled = isFeatureEnabled('owner-reveal')

  const onClose = async () => {
    await clearSelected()
  }

  const propertiesDistributions = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_OWNER') {
      return []
    }

    return getPropertiesDistribution(selectedEntity.data)
  }, [selectedEntity])

  const distributions = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_OWNER') {
      return {}
    }
    return parseEntityDistribution(selectedEntity.data)
  }, [selectedEntity])

  useEffect(() => {
    if (isOwnershipEnabled) {
      return
    }
    void clearSelected()
  }, [isOwnershipEnabled])

  if (!selectedEntity || selectedEntity.type !== 'SELECTED_OWNER') {
    return null
  }

  const owner = selectedEntity.data

  return (
    <>
      <ErrorBoundary wrap>
        <SidebarEntityHeader
          badge={formatString(owner.type, 'name')}
          title={formatString(owner.name, 'name')}
          subtitle={formatString(owner.address, 'address')}
          amountOfContacts={owner.amountOfContacts}
          amountOfParcels={owner.amountOfParcels}
          distributions={distributions}
          propertiesDistributions={propertiesDistributions}
          image={HeaderBG}
          iconBackground={ICON_BG_COLOR}
          iconBorderColor={ICON_BORDER_COLOR}
          icon={<Briefcase color={Colors.primaryRed800} />}
          backText={showNavigationActions ? getBackText() : undefined}
          onBackClick={showNavigationActions ? goBack : undefined}
          onClose={showNavigationActions ? onClose : undefined}
        />
      </ErrorBoundary>
      <Container>
        <ErrorBoundary wrap>
          <MapExplorerPanelActions
            copy={{
              label: 'Address',
              text: owner.address,
            }}
          />
        </ErrorBoundary>
        <ErrorBoundary wrap>
          <OwnerPortfolio />
        </ErrorBoundary>
        <Divider />
        <ReportsContainer>
          <ReportsTitle variant="title2" color={Colors.neutral950}>
            Reports
          </ReportsTitle>
          <ReportsContent>
            <ErrorBoundary wrap>
              <SidebarInsightsMenu title="Owner Reports" items={OWNER_REPORTS_MENU_ITEMS} />
            </ErrorBoundary>
            <ErrorBoundary wrap>
              <SidebarInsightsMenu title="Portfolio Reports" items={PORTFOLIO_REPORTS_MENU_ITEMS} />
            </ErrorBoundary>
          </ReportsContent>
        </ReportsContainer>
        <Divider />
        <ErrorBoundary wrap>
          <OwnerParcels />
        </ErrorBoundary>
      </Container>
    </>
  )
}
